import { registerTool, unregisterAgentTool } from '#layers/core/app/runtime/utils/agentTools';
import { ToolCallFailure } from '#layers/core/app/runtime/utils/agentToolsRegistry';

// Nazwa rejestracji składa się z tej przestrzeni i wartości propsa `tool`, a nie z tekstu przycisku:
// tekst jest przetłumaczony ($t w widoku), więc nazwa oparta na nim zmieniałaby się z językiem panelu
// i agent nie mógłby jej zapamiętać. Przestrzeń jest wymagana, bo rejestr odrzuca nazwę bez kropki
//
// Nazwa nie jest kwalifikowana widokiem, bo rejestracja idzie na poziom ulotny - w danej chwili
// zarejestrowane są przyciski jednego, otwartego widoku. Kolizja jest możliwa tylko w obrębie jednego
// ekranu i jest wtedy pomyłką autora widoku, który oznaczył dwa przyciski tą samą wartością
const TOOL_NAMESPACE = 'button';

// Komponent podaje stan przycisku funkcjami, a nie wartościami - odczyt musi nastąpić w chwili
// wywołania narzędzia, nie w chwili rejestracji: przycisk potrafi zmienić nieaktywność wiele razy bez
// odmontowania (stan formularza, ładowanie), a agent ma widzieć to samo co użytkownik
export interface ActionButtonToolHost
{
    tool: () => string;
    label: () => string;
    disabled: () => boolean;
    confirmation: () => boolean;
    click: () => void;
    warn: (message: string) => void;
}

interface ActionButtonToolArguments
{
    confirm: boolean;
}

type ActionButtonToolParseResult = { success: true; data: ActionButtonToolArguments } | { success: false; error: { message: string } };

// Rejestr wymaga w czasie działania obiektu z metodą safeParse, nie Zoda (agentToolsRegistry.ts),
// a tutaj sprawdzenie kształtu to dwa warunki. Brak argumentów jest poprawnym wywołaniem, bo przycisk
// bez potwierdzenia niczego od agenta nie potrzebuje
//
// Argument confirm zostaje przyjmowany, choć niczego już nie wykonuje: stare wywołania z confirm: true
// mają dostać odmowę potwierdzenia, a nie invalid-arguments, który ukryłby, dlaczego przycisk nie działa
const argumentsSchema = {
    safeParse(args: unknown): ActionButtonToolParseResult
    {
        if (args === null || args === undefined)
        {
            return { success: true, data: { confirm: false } };
        }

        if (typeof args !== 'object' || Array.isArray(args))
        {
            return { success: false, error: { message: 'Argumenty narzędzia przycisku muszą być obiektem albo mogą zostać pominięte.' } };
        }

        const candidate = args as Record<string, unknown>;

        // Wartość inna niż logiczna jest odrzucana, a nie traktowana jako brak potwierdzenia:
        // wywołanie z confirm: "tak" jest pomyłką w kształcie argumentu i agent ma o niej usłyszeć
        // wprost, zamiast dostać odmowę mówiącą, że nie potwierdził
        if ('confirm' in candidate && typeof candidate.confirm !== 'boolean')
        {
            return { success: false, error: { message: 'Argument "confirm" musi być wartością logiczną - potwierdzeniem wykonania akcji.' } };
        }

        return { success: true, data: { confirm: candidate.confirm === true } };
    }
};

function labelOf(host: ActionButtonToolHost): string
{
    const label = host.label().trim();

    return label.length > 0 ? label : host.tool();
}

// Opis bierze przetłumaczony tekst przycisku, choć nazwa go brać nie może. Opis nie jest
// identyfikatorem - agent nie odwołuje się do niego przy wywołaniu, więc jego zmiana z językiem
// panelu nic nie psuje. W zamian daje jedyną rzecz, po której agent rozpozna przycisk na ekranie:
// napis, który widzi na nim użytkownik. Przycisk bez tekstu (sama ikona) opisany jest wartością propsa
function descriptionOf(host: ActionButtonToolHost): string
{
    return `Klika przycisk "${labelOf(host)}" w otwartym widoku panelu, tak jak zrobiłby to użytkownik. Nie czeka na skutek akcji ani go nie zwraca. Przycisku, który pyta użytkownika o potwierdzenie, narzędzie nie wykona - odmówi (button-needs-confirmation, a z argumentem confirm: needs-confirmation); poproś wtedy użytkownika, żeby sam kliknął przycisk i potwierdził.`;
}

// ToolCallFailure rzucana jest wyłącznie stąd - z funkcji wołanej tylko przez handler tego narzędzia -
// więc żaden inny wołający nie dostanie odmowy z tymi powodami
function callButton(host: ActionButtonToolHost, name: string, args: ActionButtonToolArguments): { button: string }
{
    // Nieaktywność sprawdzana przed potwierdzeniem: przycisk nieaktywny nie wykona akcji nawet
    // z potwierdzeniem, więc pytanie o potwierdzenie byłoby wtedy myleniem agenta
    //
    // Jeden powód odmowy dla wszystkich przyczyn nieaktywności. Props nieaktywności to jedna wartość
    // logiczna i nie przenosi informacji, czy chodzi o brak uprawnień, czy o niezmieniony formularz -
    // rozdzielenie powodów wymagałoby wiedzy, której komponent nie ma
    if (host.disabled())
    {
        throw new ToolCallFailure('button-disabled', `Przycisk "${labelOf(host)}" jest na ekranie nieaktywny - użytkownik również nie mógłby go teraz kliknąć. Powodem jest brak uprawnień albo stan formularza; przycisk nie odróżnia tych przypadków.`);
    }

    // Dialog stoi przed akcją dla człowieka, a argument wywołania nie jest kliknięciem "tak" w panelu,
    // więc agent przycisku z dialogiem nie wykonuje wcale - tak samo jak list.action na akcji z confirms.
    // Dwa kody, bo wywołanie bez confirm dostawało tę odmowę już wcześniej i ma dostawać ją dalej;
    // needs-confirmation przychodzi tam, gdzie przycisk dotąd się wykonywał
    //
    // Stan czytany w chwili wywołania, nie rejestracji: dialog bywa warunkowy, a narzędzie ma odmówić
    // dokładnie wtedy, gdy człowiek po kliknięciu zobaczyłby pytanie
    if (host.confirmation())
    {
        if (args.confirm !== true)
        {
            throw new ToolCallFailure('button-needs-confirmation', `Przycisk "${labelOf(host)}" pyta użytkownika o potwierdzenie w panelu, więc to narzędzie go nie klika. Poproś użytkownika, żeby sam kliknął ten przycisk i potwierdził.`);
        }

        throw new ToolCallFailure('needs-confirmation', `Przycisk "${labelOf(host)}" pyta użytkownika o potwierdzenie w panelu, a argument confirm tego potwierdzenia nie zastępuje - narzędzie nie wykonuje przycisków stojących za dialogiem. Poproś użytkownika, żeby sam kliknął ten przycisk i potwierdził.`);
    }

    host.click();

    return { button: name };
}

// Rejestracja i wyrejestrowanie w zaczepach cyklu życia komponentu. Wydzielone z ActionButton.vue,
// bo komponentu nie da się zamontować w projekcie testowym `unit` (environment `node`, bez wtyczki
// Vue dla plików .vue i bez drzewa dokumentu), a bez montowania nie dowiodłoby się, że wpis faktycznie
// znika po odmontowaniu - tutaj dowodzi tego prawdziwy komponent Vue zbudowany w teście
export function useActionButtonAgentTool(host: ActionButtonToolHost): void
{
    // Nazwa zapamiętana z chwili UDANEJ rejestracji, nie odczytana ponownie przy odmontowaniu.
    // Rejestr milczy na nazwę nieistniejącą, więc ten warunek nie chroni przed brakiem wpisu - chroni
    // przed zdjęciem wpisu należącego do kogoś innego: przycisk, który przegrał kolizję nazw, nie może
    // przy odmontowaniu wyrejestrować przycisku, który tę samą nazwę zarejestrował skutecznie
    let registered: string | null = null;

    // Właściciel wpisu: Vue montuje nowy widok, zanim odmontuje stary, więc stary przycisk zdejmowałby
    // przy odmontowaniu wpis o tej samej nazwie zarejestrowany właśnie przez nowy
    const owner = {};

    onMounted(() =>
    {
        const tool = host.tool();

        // Bez propsa `tool` komponent nie rejestruje niczego. Przycisk jest w kilkuset widokach,
        // a w wierszach tabeli po wiele razy na jednym ekranie - rejestracja bez jawnego oznaczenia
        // byłaby kolizją nazw w każdym takim widoku
        if (tool.length === 0)
        {
            return;
        }

        const name = `${TOOL_NAMESPACE}.${tool}`;

        try
        {
            registerTool(
                name,
                descriptionOf(host),
                // Rzutowanie na typ schematu z sygnatury rejestru: rejestr sprawdza w czasie działania
                // samą metodę safeParse, ale typuje ten parametr Zodem
                argumentsSchema as unknown as Parameters<typeof registerTool>[2],
                args => callButton(host, name, args as ActionButtonToolArguments),
                {
                    level: 'ephemeral',
                    owner,
                    argumentsDescription: 'bez argumentów'
                }
            );

            registered = name;
        }
        catch (error)
        {
            // Kolizja nazw nie może wysypać widoku - rejestr rzuca przy duplikacie, a to jest wyjątek
            // z zaczepu montowania, więc przerwałby renderowanie ekranu z powodu narzędzia dla agenta.
            // Ostrzeżenie zostaje widoczne w mechanizmie logowania panelu: to nie jest ciche pominięcie
            host.warn(`Przycisk oznaczony jako "${tool}" nie został zarejestrowany jako narzędzie agenta: ${error instanceof Error ? error.message : String(error)} Sprawdź, czy dwa przyciski na tym ekranie nie mają tej samej wartości propsa "tool".`);
        }
    });

    // Poziom ulotny czyści się sam przy zmianie widoku, ale przycisk potrafi zniknąć bez zmiany trasy:
    // warunek w szablonie, rozwijane menu, okienko modalne, przełączona zakładka
    onUnmounted(() =>
    {
        if (registered === null)
        {
            return;
        }

        unregisterAgentTool(registered, owner);
        registered = null;
    });
}
